import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Comptes d'exécution — proxy de sortie et mode d'exécution.
 *
 * Un compte en mode `plugin` (tous les comptes aujourd'hui) est piloté par
 * l'extension dans le navigateur du recruteur et sort par SON IP : il n'y a
 * rien à configurer ici. Un compte en mode `server` est tenu par un pod
 * browserfarm, et son IP de sortie devient un choix.
 *
 * Le mot de passe du proxy n'est JAMAIS renvoyé par l'API — ni en clair ni
 * chiffré. `password_set` dit seulement s'il y en a un. L'omettre à la
 * sauvegarde préserve celui qui est stocké ; envoyer `""` l'efface.
 */

export type ExecutionMode = "plugin" | "server";

export interface IspProxyOut {
  provider?: string | null;
  endpoint?: string | null;
  username?: string | null;
  sticky_session_id?: string | null;
  password_set: boolean;
}

export interface ProxyGeo {
  country?: string | null;
  city?: string | null;
  lat?: number | null;
  lng?: number | null;
  timezone?: string | null;
  locale?: string | null;
}

export interface ProxyConfigOut {
  isp_proxy?: IspProxyOut | null;
  geo?: ProxyGeo | null;
}

export interface ExecutionAccount {
  subscription_id: number;
  user_id?: string | null;
  execution_mode: ExecutionMode;
  proxy?: ProxyConfigOut | null;
  challenged_at?: string | null;
  challenge_kind?: string | null;
}

export interface ProxyConfigIn {
  isp_proxy: {
    provider?: string | null;
    endpoint: string;
    username?: string | null;
    /** Omettre pour conserver le mot de passe stocké. */
    password?: string;
    sticky_session_id?: string | null;
  };
  geo?: ProxyGeo | null;
}

export interface ProxyTestResult {
  /** « cette sortie est utilisable » — pas « l'appel HTTP a rendu 200 ». */
  ok: boolean;
  seen_ip?: string | null;
  seen_country?: string | null;
  declared_country?: string | null;
  country_match?: boolean | null;
  seen_timezone?: string | null;
  timezone_match?: boolean | null;
  /** ASN / org of the exit (a datacenter ASN is the signal to look for). */
  asn?: string | null;
  city?: string | null;
  /** Only set by a probe that types the address (paid probes). */
  hosting?: boolean | null;
  error?: string | null;
}

async function unwrap<T>(res: Response | undefined): Promise<T> {
  if (!res?.ok) {
    let detail = `Erreur ${res?.status ?? "?"}`;
    try {
      const body = await res?.json();
      if (body?.detail) detail = `${detail} : ${body.detail}`;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json();
}

export async function getExecutionAccountByUser(
  userId: string,
  navigate: NavigateFunction,
): Promise<ExecutionAccount> {
  const res = await apiFetch(
    `${API_URL}/execution-accounts/by-user/${encodeURIComponent(userId)}`,
    { method: "GET" },
    navigate,
  );
  return unwrap<ExecutionAccount>(res);
}

export async function putProxyConfig(
  subscriptionId: number,
  payload: ProxyConfigIn,
  navigate: NavigateFunction,
): Promise<ProxyConfigOut> {
  const res = await apiFetch(
    `${API_URL}/execution-accounts/${subscriptionId}/proxy`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate,
  );
  return unwrap<ProxyConfigOut>(res);
}

export async function deleteProxyConfig(
  subscriptionId: number,
  navigate: NavigateFunction,
): Promise<void> {
  const res = await apiFetch(
    `${API_URL}/execution-accounts/${subscriptionId}/proxy`,
    { method: "DELETE" },
    navigate,
  );
  if (!res?.ok && res?.status !== 204) {
    await unwrap<void>(res);
  }
}

export async function testProxy(
  subscriptionId: number,
  navigate: NavigateFunction,
): Promise<ProxyTestResult> {
  const res = await apiFetch(
    `${API_URL}/execution-accounts/${subscriptionId}/proxy/test`,
    { method: "POST" },
    navigate,
  );
  return unwrap<ProxyTestResult>(res);
}

export async function setExecutionMode(
  subscriptionId: number,
  mode: ExecutionMode,
  navigate: NavigateFunction,
): Promise<{ subscription_id: number; execution_mode: ExecutionMode; previous: ExecutionMode }> {
  const res = await apiFetch(
    `${API_URL}/execution-accounts/${subscriptionId}/execution-mode`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode }),
    },
    navigate,
  );
  return unwrap(res);
}

/** Lift the LinkedIn-challenge quarantine (after checking the account). */
export async function clearChallenge(
  subscriptionId: number,
  navigate: NavigateFunction,
): Promise<{ subscription_id: number; cleared: boolean; previous_kind?: string | null }> {
  const res = await apiFetch(
    `${API_URL}/execution-accounts/${subscriptionId}/challenge/clear`,
    { method: "POST" },
    navigate,
  );
  return unwrap(res);
}
